/**
 * Template Card Component
 * Displays a single email template card with preview image
 *
 * @file src/components/email/templates/TemplateCard.tsx
 * @created 2025-10-22
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmailTemplate } from '@/api/email';
import { Mail, Sparkles, Edit, Copy, Eye, Trash2, MoreVertical, Camera } from 'lucide-react';

interface TemplateCardProps {
  template: EmailTemplate;
  onSelect: (template: EmailTemplate) => void;
  onDelete?: (template: EmailTemplate) => void;
  onDuplicate?: (template: EmailTemplate) => void;
  onPreview?: (template: EmailTemplate) => void;
  onCapturePreview?: (template: EmailTemplate) => void;
  showCustomizeButton?: boolean;
  actionLabel?: string;
  userRole?: string;  // For access control on capture preview
}

/**
 * TemplateCard Component
 *
 * Displays a template card with:
 * - Preview image (or placeholder)
 * - Template name and description
 * - Category badge
 * - Usage count (for user templates)
 * - Select/Customize button
 *
 * @example
 * <TemplateCard
 *   template={template}
 *   onSelect={(template) => handleTemplateClick(template)}
 *   showCustomizeButton={true}
 * />
 */
const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onSelect,
  onDelete,
  onDuplicate,
  onPreview,
  onCapturePreview,
  showCustomizeButton = false,
  actionLabel,
  userRole
}) => {
  // Determine if user can capture preview for this template
  // System templates: Must be customized first (capture from PreviewDialog instead)
  // User templates: Can capture directly from template card
  const canCapturePreview = onCapturePreview && !template.is_system_template;
  const getCategoryColor = (category?: string) => {
    switch (category) {
      case 'promotional':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'newsletter':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'transactional':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'trigger':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getCategoryIcon = (category?: string) => {
    switch (category) {
      case 'promotional':
      case 'newsletter':
        return <Sparkles className="h-3 w-3" />;
      case 'transactional':
      case 'trigger':
        return <Mail className="h-3 w-3" />;
      default:
        return null;
    }
  };

  return (
    <Card className="group hover:shadow-md transition-all duration-200 hover:border-primary/50 cursor-pointer">
      <CardContent className="p-0">
        {/* Preview Image */}
        <div
          className="relative w-full aspect-[4/3] bg-gradient-to-br from-gray-50 to-gray-100 rounded-t-lg overflow-hidden"
          onClick={() => onSelect(template)}
        >
          {template.preview_image_url ? (
            <img
              src={template.preview_image_url}
              alt={template.template_name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <div className="text-center">
                <Mail className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No Preview</p>
              </div>
            </div>
          )}

          {/* System Template Badge */}
          {template.is_system_template && (
            <div className="absolute top-2 right-2">
              <Badge className="bg-white/90 text-primary border-primary/20 backdrop-blur-sm">
                <Sparkles className="h-3 w-3 mr-1" />
                System
              </Badge>
            </div>
          )}

          {/* Action Menu (shows on hover, top-left if system template, otherwise top-right) */}
          {(onDelete || onDuplicate || onPreview || canCapturePreview) && (
            <div className={`absolute top-2 ${template.is_system_template ? 'left-2' : 'right-2'} opacity-0 group-hover:opacity-100 transition-opacity`}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 bg-white/90 hover:bg-white shadow-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(template);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    {template.is_system_template ? 'Customize' : 'Edit'}
                  </DropdownMenuItem>

                  {onDuplicate && !template.is_system_template && (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onDuplicate(template);
                      }}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Duplicate
                    </DropdownMenuItem>
                  )}

                  {onPreview && (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onPreview(template);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Preview
                    </DropdownMenuItem>
                  )}

                  {canCapturePreview && (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onCapturePreview!(template);
                      }}
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Capture Preview
                    </DropdownMenuItem>
                  )}

                  {onDelete && !template.is_system_template && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(template);
                        }}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {/* Template Info */}
        <div className="p-4">
          {/* Name and Category */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3
              className="font-medium text-sm line-clamp-1 flex-1"
              title={template.template_name}
            >
              {template.template_name}
            </h3>
            {template.category && (
              <Badge
                variant="outline"
                className={`text-xs shrink-0 ${getCategoryColor(template.category)}`}
              >
                {getCategoryIcon(template.category)}
                <span className="ml-1 capitalize">{template.category}</span>
              </Badge>
            )}
          </div>

          {/* Description */}
          {template.description && (
            <p className="text-xs text-gray-600 line-clamp-2 mb-3">
              {template.description}
            </p>
          )}

          {/* Footer: Usage Count + Action Button */}
          <div className="flex items-center justify-between gap-2">
            {/* Usage Count (for user templates) */}
            {!template.is_system_template && template.usage_count !== undefined && (
              <span className="text-xs text-gray-500">
                Used {template.usage_count} {template.usage_count === 1 ? 'time' : 'times'}
              </span>
            )}

            {/* Spacer */}
            <div className="flex-1" />

            {/* Action Button */}
            <Button
              size="sm"
              variant={showCustomizeButton ? 'default' : 'outline'}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(template);
              }}
              className="text-xs"
            >
              {actionLabel || (showCustomizeButton ? 'Customize' : (template.is_system_template ? 'Use Template' : 'Edit Template'))}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TemplateCard;
